// webapp/src/utils/analytics.js — PostHog Product Analytics
// ─────────────────────────────────────────────────────────────────────────
// S-003 (2026-05-26): PostHog analytics wrapper for webapp.
// Provides product analytics, feature flags, session replays, and user
// behavior tracking. Defensive pattern — silent no-op if PostHog disabled
// or token missing (mirrors mobile crashReporting.js pattern for parity).
//
// SDK source priority:
//   1. REACT_APP_POSTHOG_KEY env var → enabled
//   2. None / placeholder → disabled (silent no-op everywhere)
//
// What PostHog gives you:
//   • Autocapture: clicks, pageviews, form submits (no manual instrumentation)
//   • Custom events: trackEvent('trade_placed', { symbol, amount })
//   • User identification: identifyUser(userId) after login
//   • Session replays: 10% sample (cost-efficient, privacy-safe with masking)
//   • Feature flags: getFeatureFlag(flagName) for A/B tests
//   • Funnels: see drop-off in signup → first-trade flow
//
// Production-grade settings:
//   • capture_pageview: true       — autocapture every navigation
//   • capture_pageleave: true      — measure session duration
//   • person_profiles: 'identified_only' — don't track anonymous users (GDPR)
//   • disable_session_recording in dev — only record in production
//   • mask_all_text: false (autocapture safe by default — opt-in masking via DOM)
//   • respect_dnt: true            — honor browser Do-Not-Track
//
// Rule G 4Q: All GREEN — additive observability layer, zero functional changes
// Rule Q (Parity): mobile gets identical API surface (initAnalytics, etc.)
// Rule L: defensive — never crashes the app if PostHog fails to load
// ─────────────────────────────────────────────────────────────────────────

// Lazy-require pattern (matches mobile crashReporting.js) — never crash on
// missing/broken PostHog package. import.meta would fail in CRA so we use
// dynamic require() through try/catch.

let _initialized = false;
let _enabled = false;

/**
 * Get PostHog project token from env (priority order).
 * Returns null if not configured — analytics stays disabled (silent no-op).
 */
function getToken() {
  const token = process.env.REACT_APP_POSTHOG_KEY;
  if (!token || token.includes('PLACEHOLDER') || token.includes('YOUR_')) {
    return null;
  }
  return token;
}

function getHost() {
  return process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
}

/**
 * Initialize PostHog. Call once at app startup (webapp/src/index.js).
 * Idempotent — safe to call multiple times.
 */
export function initAnalytics() {
  if (_initialized) return _enabled;
  _initialized = true;

  const token = getToken();
  if (!token) {
    // Silent: PostHog disabled when no token (dev/local default)
    return false;
  }

  try {
    // eslint-disable-next-line global-require
    const posthog = require('posthog-js').default || require('posthog-js');

    posthog.init(token, {
      api_host: getHost(),
      // S-003: Production-grade defaults (per PostHog docs 2026-01-30)
      defaults: '2026-01-30',
      // Track only identified users for GDPR compliance — anonymous users
      // can still trigger events, but their profiles aren't created until
      // identifyUser() is called.
      person_profiles: 'identified_only',
      // Autocapture: clicks, form submits, pageviews — no manual instrumentation
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
      // Session replay: only in production, 10% sample
      disable_session_recording: process.env.NODE_ENV !== 'production',
      session_recording: {
        // Privacy: mask all text inputs by default (passwords, emails, names)
        maskAllInputs: true,
        maskTextSelector: '.posthog-mask', // Add this class to extra-sensitive UI
      },
      // Respect browser Do-Not-Track header
      respect_dnt: true,
      // Reduce noise: don't capture for these elements
      autocapture_settings: {
        css_selector_allowlist: [],
        url_allowlist: [],
        dom_event_allowlist: ['click', 'submit', 'change'],
      },
      // Cross-domain support if we add subdomains later
      cross_subdomain_cookie: false,
      // Skip in development
      loaded: (ph) => {
        if (process.env.NODE_ENV !== 'production') {
          ph.opt_out_capturing(); // Stop capturing in dev
        }
      },
    });

    _enabled = true;
    return true;
  } catch (err) {
    // PostHog package not installed or init failed — silent no-op
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[PostHog] Init failed (this is normal in dev):', err.message);
    }
    return false;
  }
}

/**
 * Track a custom event. Safe to call even if PostHog is disabled (silent no-op).
 *
 * @param {string} eventName - e.g. 'trade_placed', 'signup_completed'
 * @param {object} [properties] - event metadata, e.g. { symbol: 'BTC', amount: 100 }
 */
export function trackEvent(eventName, properties = {}) {
  if (!_enabled) return;
  try {
    // eslint-disable-next-line global-require
    const posthog = require('posthog-js').default || require('posthog-js');
    posthog.capture(eventName, properties);
  } catch (_) { /* silent */ }
}

/**
 * Identify the logged-in user. Call after successful login.
 * userId should be your internal user UUID (anonymized — NOT email).
 *
 * @param {string} userId - internal user ID (UUID preferred)
 * @param {object} [traits] - non-PII user properties, e.g. { plan: 'pro' }
 */
export function identifyUser(userId, traits = {}) {
  if (!_enabled || !userId) return;
  try {
    // eslint-disable-next-line global-require
    const posthog = require('posthog-js').default || require('posthog-js');
    posthog.identify(String(userId), traits);
  } catch (_) { /* silent */ }
}

/**
 * Clear user context on logout. Important for shared-device scenarios.
 */
export function resetAnalytics() {
  if (!_enabled) return;
  try {
    // eslint-disable-next-line global-require
    const posthog = require('posthog-js').default || require('posthog-js');
    posthog.reset();
  } catch (_) { /* silent */ }
}

/**
 * Check a feature flag value (boolean) for the current user.
 * Falls back to defaultValue if PostHog is disabled or flag not set.
 *
 * @param {string} flagKey
 * @param {boolean} [defaultValue=false]
 * @returns {boolean}
 */
export function getFeatureFlag(flagKey, defaultValue = false) {
  if (!_enabled) return defaultValue;
  try {
    // eslint-disable-next-line global-require
    const posthog = require('posthog-js').default || require('posthog-js');
    return posthog.isFeatureEnabled(flagKey) ?? defaultValue;
  } catch (_) {
    return defaultValue;
  }
}

/**
 * Diagnostic — for development/debugging only.
 */
export function isAnalyticsEnabled() {
  return _enabled;
}
